import { useCallback, useEffect, useState } from "react";
import { AlertTriangle, CheckCircle2, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "../../lib/api";
import { fmtDate, fmtDateTime, label } from "../../lib/format";
import { useAuth } from "../../context/AuthContext";
import { Card, Empty, Field, Td, Th } from "../common/UI";
import { Button } from "../ui/button";

const save = (p, msg, reload) => p.then(() => { toast.success(msg); reload(); }).catch((e) => toast.error(errMsg(e)));
const txt = (f, set, k, testId, rows = 2) => <textarea rows={rows} className="otms-input" value={f[k] || ""} onChange={(e) => set({ ...f, [k]: e.target.value })} data-testid={testId} />;
const dateIn = (f, set, k, testId) => <input type="date" className="otms-input" value={f[k] || ""} onChange={(e) => set({ ...f, [k]: e.target.value || null })} data-testid={testId} />;

export const DueBadge = ({ due, today, done, testId }) => {
  if (done || !due) return null;
  const days = Math.round((new Date(due) - new Date(today)) / 86400000);
  const cls = days < 0 ? "bg-red-100 text-red-800" : days <= 3 ? "bg-amber-100 text-amber-800" : "bg-slate-100 text-slate-600";
  return <span className={`rounded px-1.5 py-0.5 text-[10px] font-semibold ${cls}`} data-testid={testId}>{days < 0 ? `${-days} day(s) overdue` : days === 0 ? "Due today" : `${days} day(s) left`}</span>;
};

function Executive({ d, m, reload }) {
  const { can } = useAuth();
  const ea = d.executive;
  const [f, setF] = useState(ea || { received_date: d.today });
  const [vote, setVote] = useState("");
  const edit = can("exec.record") && !ea?.override_status && ["approved_sanggunian", "awaiting_executive"].includes(d.status);
  const submit = () => save(api.put(`/measures/${m.id}/executive-action`, {
    received_date: f.received_date, deadline_date: f.deadline_date || null, action: f.action || null, action_date: f.action_date || null,
    vetoed_items: f.vetoed_items, veto_reasons: f.veto_reasons, remarks: f.remarks,
  }), "Executive action saved", reload);
  const vetoed = ["vetoed", "partially_vetoed"].includes(ea?.action);
  return (
    <Card title="Executive action (Local Chief Executive)" testId="exec-card"
      actions={ea && <DueBadge due={ea.deadline_date} today={d.today} done={!!ea.action} testId="exec-due-badge" />}>
      {!ea && !edit && <Empty text="Executive action is recorded after Sanggunian approval." />}
      {(ea || edit) && (
        <fieldset disabled={!edit} className="grid gap-3 md:grid-cols-3" data-testid="exec-fields">
          <Field label="Date received by LCE">{dateIn(f, setF, "received_date", "exec-received")}</Field>
          <Field label="Action deadline" hint={`Default: ${d.settings.lce_days} days after receipt${ea?.deadline_manual ? " · set manually" : ""}. Leave blank to compute.`}>{dateIn(f, setF, "deadline_date", "exec-deadline")}</Field>
          <Field label="Action">
            <select className="otms-input" value={f.action || ""} onChange={(e) => setF({ ...f, action: e.target.value || null })} data-testid="exec-action-select">
              <option value="">Pending</option>{["approved", "vetoed", "partially_vetoed", "deemed_approved"].map((a) => <option key={a} value={a}>{label(a)}</option>)}
            </select>
          </Field>
          {f.action && <Field label="Date of action">{dateIn(f, setF, "action_date", "exec-action-date")}</Field>}
          {["vetoed", "partially_vetoed"].includes(f.action) && <Field label="Reasons for veto" className="md:col-span-2">{txt(f, setF, "veto_reasons", "exec-veto-reasons")}</Field>}
          {f.action === "partially_vetoed" && <Field label="Vetoed items / provisions" className="md:col-span-3">{txt(f, setF, "vetoed_items", "exec-vetoed-items")}</Field>}
          <Field label="Remarks" className="md:col-span-3">{txt(f, setF, "remarks", "exec-remarks")}</Field>
          {edit && <div className="md:col-span-3"><Button size="sm" className="bg-sky-600 hover:bg-sky-700" onClick={submit} data-testid="exec-save">Save executive action</Button></div>}
        </fieldset>
      )}
      {vetoed && (
        <div className={`mt-4 rounded border p-3 text-xs ${ea.override_status === "overridden" ? "border-emerald-200 bg-emerald-50" : "border-amber-200 bg-amber-50"}`} data-testid="override-panel">
          <div className="mb-2 font-semibold text-slate-900">Veto override ({label(d.settings.veto_override_rule)} of the Sanggunian)</div>
          {ea.override_status ? (
            <div data-testid="override-status">Veto <b>{ea.override_status}</b> on {fmtDate(ea.override_date)} — vote {ea.override_vote?.yes_count}-{ea.override_vote?.no_count}.</div>
          ) : can("vote.record") && d.status === "executive_recorded" ? (
            <div className="flex flex-wrap gap-2">
              <select className="otms-input h-8 flex-1 text-xs" value={vote} onChange={(e) => setVote(e.target.value)} data-testid="override-vote-select">
                <option value="">Select the finalized override vote…</option>
                {d.votes.map((v) => <option key={v.id} value={v.id}>{v.question.slice(0, 80)} — {v.yes_count}-{v.no_count} ({label(v.majority_rule)}, {v.declared_outcome})</option>)}
              </select>
              <Button size="sm" variant="outline" disabled={!vote} onClick={() => save(api.post(`/executive-actions/${ea.id}/override`, { vote_id: vote }), "Override recorded", reload)} data-testid="override-save">Record override result</Button>
            </div>
          ) : <div>Awaiting an override vote in session.</div>}
        </div>
      )}
    </Card>
  );
}

function Reviews({ d, m, reload }) {
  const { can } = useAuth();
  const [n, setN] = useState({ reviewing_body: d.settings.review_body, submitted_date: d.today });
  const [edit, setEdit] = useState(null);
  const ok = can("review.record") && ["executive_recorded", "submitted_external_review", "review_findings_received", "corrective_action_required"].includes(d.status);
  const update = () => save(api.put(`/reviews/${edit.id}`, { ...edit, deadline_date: edit.deadline_date || null }), "Review updated", () => { setEdit(null); reload(); });
  return (
    <Card title="External review" testId="review-card">
      {d.reviews.length === 0 && <Empty text="No external review recorded." />}
      {d.reviews.map((r) => (
        <div key={r.id} className="mb-3 rounded border border-slate-200 p-3 text-xs" data-testid={`review-${r.id}`}>
          <div className="flex flex-wrap items-center gap-2">
            <b>{r.reviewing_body}</b> · submitted {fmtDate(r.submitted_date)} · due {fmtDate(r.deadline_date)}{r.deadline_manual && " (manual)"}
            <span className="rounded bg-slate-100 px-1.5 py-0.5 font-medium" data-testid={`review-status-${r.id}`}>{label(r.status)}</span>
            <DueBadge due={r.deadline_date} today={d.today} done={r.status !== "pending"} testId={`review-due-${r.id}`} />
            {ok && <Button size="sm" variant="ghost" className="ml-auto h-7" onClick={() => setEdit({ ...r })} data-testid={`review-edit-${r.id}`}>Record result</Button>}
          </div>
          {r.findings && <div className="mt-1 whitespace-pre-line text-slate-600">{r.findings}</div>}
          {edit?.id === r.id && (
            <div className="mt-3 grid gap-2 md:grid-cols-3">
              <Field label="Result">
                <select className="otms-input" value={edit.status} onChange={(e) => setEdit({ ...edit, status: e.target.value })} data-testid="review-status-select">
                  {["pending", "approved", "approved_with_findings", "disapproved", "corrective_action", "deemed_approved"].map((s) => <option key={s} value={s}>{label(s)}</option>)}
                </select>
              </Field>
              <Field label="Date of findings">{dateIn(edit, setEdit, "findings_date", "review-findings-date")}</Field>
              <Field label="Review deadline" hint={`Default ${d.settings.review_days} days`}>{dateIn(edit, setEdit, "deadline_date", "review-deadline")}</Field>
              <Field label="Findings" className="md:col-span-3">{txt(edit, setEdit, "findings", "review-findings")}</Field>
              <div className="md:col-span-3"><Button size="sm" className="bg-sky-600 hover:bg-sky-700" onClick={update} data-testid="review-save">Save result</Button></div>
            </div>
          )}
        </div>
      ))}
      {ok && !d.reviews.some((r) => r.status === "pending") && (
        <div className="grid gap-2 border-t pt-3 md:grid-cols-[1fr_160px_160px_auto] md:items-end">
          <Field label="Reviewing authority"><input className="otms-input" value={n.reviewing_body} onChange={(e) => setN({ ...n, reviewing_body: e.target.value })} data-testid="review-body-input" /></Field>
          <Field label="Date submitted">{dateIn(n, setN, "submitted_date", "review-submitted")}</Field>
          <Field label="Deadline (optional)">{dateIn(n, setN, "deadline_date", "review-new-deadline")}</Field>
          <Button size="sm" variant="outline" onClick={() => save(api.post(`/measures/${m.id}/reviews`, { ...n, deadline_date: n.deadline_date || null }), "Review submission recorded", reload)} data-testid="review-add">Record submission</Button>
        </div>
      )}
    </Card>
  );
}

function Publications({ d, m, reload }) {
  const { can } = useAuth();
  const [f, setF] = useState({ kind: "posting", start_date: d.today });
  const p = d.posting;
  const ok = can("measure.publish") && d.status === "publication_pending";
  return (
    <Card title="Posting and publication" testId="publication-card"
      actions={<span className={`flex items-center gap-1 text-[11px] font-medium ${p.met ? "text-emerald-700" : "text-amber-700"}`} data-testid="posting-requirement">
        {p.met ? <CheckCircle2 className="h-3.5 w-3.5" /> : <AlertTriangle className="h-3.5 w-3.5" />}
        {p.places_with_proof}/{p.min_places} places with proof{p.newspaper_required && ` · newspaper ${p.newspapers_with_proof ? "done" : "required"}`}</span>}>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[640px]" data-testid="publications-table">
          <thead><tr><Th>Type</Th><Th>Place / outlet</Th><Th>Dates</Th><Th>Proof</Th><Th /></tr></thead>
          <tbody>
            {d.publications.map((r) => (
              <tr key={r.id}>
                <Td>{label(r.kind)}</Td><Td>{r.place_or_outlet}{r.notes && <div className="text-[11px] text-slate-400">{r.notes}</div>}</Td>
                <Td>{fmtDate(r.start_date)}{r.end_date && ` – ${fmtDate(r.end_date)}`}</Td>
                <Td>{r.proof_title || <span className="text-amber-700">No proof</span>}</Td>
                <Td>{ok && <button onClick={() => save(api.delete(`/publications/${r.id}`), "Entry removed", reload)} className="text-slate-400 hover:text-red-600" data-testid={`publication-delete-${r.id}`}><Trash2 className="h-3.5 w-3.5" /></button>}</Td>
              </tr>
            ))}
          </tbody>
        </table>
        {d.publications.length === 0 && <Empty text="No posting or publication recorded." />}
      </div>
      {ok && (
        <div className="mt-3 grid gap-2 border-t pt-3 md:grid-cols-4">
          <Field label="Type">
            <select className="otms-input" value={f.kind} onChange={(e) => setF({ ...f, kind: e.target.value })} data-testid="publication-kind">
              <option value="posting">Posting (conspicuous place)</option><option value="newspaper">Newspaper publication</option><option value="online">Online / website</option>
            </select>
          </Field>
          <Field label={f.kind === "newspaper" ? "Newspaper and issue" : "Place / site"} className="md:col-span-3"><input className="otms-input" value={f.place_or_outlet || ""} onChange={(e) => setF({ ...f, place_or_outlet: e.target.value })} data-testid="publication-place" /></Field>
          <Field label="Posted / published on">{dateIn(f, setF, "start_date", "publication-start")}</Field>
          <Field label="Until (optional)">{dateIn(f, setF, "end_date", "publication-end")}</Field>
          <Field label="Proof document" hint="Upload it in the Documents tab first" className="md:col-span-2">
            <select className="otms-input" value={f.proof_document_id || ""} onChange={(e) => setF({ ...f, proof_document_id: e.target.value || null })} data-testid="publication-proof">
              <option value="">— none yet —</option>{d.documents.map((x) => <option key={x.id} value={x.id}>{x.title} ({label(x.document_type)} v{x.version_no})</option>)}
            </select>
          </Field>
          <Field label="Notes" className="md:col-span-3"><input className="otms-input" value={f.notes || ""} onChange={(e) => setF({ ...f, notes: e.target.value })} data-testid="publication-notes" /></Field>
          <div className="flex items-end"><Button size="sm" className="bg-sky-600 hover:bg-sky-700" onClick={() => save(api.post(`/measures/${m.id}/publications`, f), "Entry recorded", () => { setF({ kind: f.kind, start_date: d.today }); reload(); })} data-testid="publication-add">Add entry</Button></div>
        </div>
      )}
    </Card>
  );
}

function Effectivity({ d, m, reload }) {
  const { can, user } = useAuth();
  const e = d.effectivity;
  const [f, setF] = useState({ basis: "after_posting", newspaper_required: false, ...(e || {}), effectivity_date: e?.date_manual ? e.effectivity_date : null });
  const edit = (can("measure.publish") || can("effectivity.verify")) && !e?.verified_at && ["publication_pending", "publication_recorded"].includes(d.status);
  const submit = () => save(api.put(`/measures/${m.id}/effectivity`, { basis: f.basis, newspaper_required: !!f.newspaper_required, effectivity_date: f.effectivity_date || null, remarks: f.remarks }), "Effectivity saved", reload);
  return (
    <Card title="Effectivity" testId="effectivity-card" actions={e?.effectivity_date && <DueBadge due={e.effectivity_date} today={d.today} done={!!e.verified_at} testId="effectivity-due" />}>
      {e?.effectivity_date && (
        <div className="mb-3 text-sm" data-testid="effectivity-summary">
          Takes effect on <b>{fmtDate(e.effectivity_date)}</b>{e.computed_date && e.date_manual && ` (computed ${fmtDate(e.computed_date)}, set manually)`}
          {e.verified_at && <span className="ml-2 rounded bg-emerald-100 px-1.5 py-0.5 text-[11px] text-emerald-800" data-testid="effectivity-verified">Verified {fmtDateTime(e.verified_at)}</span>}
        </div>
      )}
      {edit && (
        <div className="grid gap-3 md:grid-cols-3">
          <Field label="Basis">
            <select className="otms-input" value={f.basis} onChange={(ev) => setF({ ...f, basis: ev.target.value })} data-testid="effectivity-basis">
              <option value="after_posting">{d.settings.effectivity_days} days after posting / publication</option><option value="upon_approval">Upon approval</option>
              <option value="specific_date">Specific date stated in the measure</option><option value="other">Other</option>
            </select>
          </Field>
          <Field label="Effectivity date" hint="Leave blank to compute from the basis">{dateIn(f, setF, "effectivity_date", "effectivity-date")}</Field>
          <label className="flex items-center gap-2 pt-6 text-xs"><input type="checkbox" checked={!!f.newspaper_required} onChange={(ev) => setF({ ...f, newspaper_required: ev.target.checked })} data-testid="effectivity-newspaper" />Newspaper publication required</label>
          <Field label="Remarks" className="md:col-span-3">{txt(f, setF, "remarks", "effectivity-remarks")}</Field>
          <div className="flex gap-2 md:col-span-3">
            <Button size="sm" className="bg-sky-600 hover:bg-sky-700" onClick={submit} data-testid="effectivity-save">Save effectivity</Button>
            {can("effectivity.verify") && d.status === "publication_recorded" && e?.effectivity_date && e.updated_by !== user.id && <Button size="sm" variant="outline" onClick={() => save(api.post(`/measures/${m.id}/effectivity/verify`), "Effectivity verified", reload)} data-testid="effectivity-verify">Verify effectivity</Button>}
          </div>
        </div>
      )}
    </Card>
  );
}

export default function PostApprovalTab({ m, onSaved }) {
  const [d, setD] = useState(null);
  const load = useCallback(() => api.get(`/measures/${m.id}/post-approval`).then((r) => setD(r.data)), [m.id]);
  useEffect(() => { load(); }, [load, m.row_version]);
  if (!d) return <div className="text-sm text-slate-500">Loading…</div>;
  const reload = () => { load(); onSaved(); };
  const k = JSON.stringify([d.executive?.updated_at, d.effectivity?.updated_at]);
  return (
    <div className="space-y-4" data-testid="post-approval-tab">
      <Executive key={`e${k}`} d={d} m={m} reload={reload} />
      <Reviews d={d} m={m} reload={reload} />
      <Publications d={d} m={m} reload={reload} />
      <Effectivity key={`f${k}`} d={d} m={m} reload={reload} />
    </div>
  );
}
